import styled, { css } from 'styled-components';
import { Text } from '~/components/ui/Text';
import { animationCoordsMap, radialAnim } from './RadialMenu.model';

const StyledContainer = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
`;

const StyledItemsCircle = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 700px;
	height: 330px;

	&.n-2 > :nth-child(1) {
		top: 50%;
		left: 50%;
		transform: translate(calc(-100% - 90px), -50%);
	}

	&.n-2 > :nth-child(2) {
		top: 50%;
		left: 50%;
		transform: translate(90px, -50%);
	}

	&.n-4 > :nth-child(1) {
		top: 50%;
		left: 50%;
		transform: translate(calc(-100% - 90px), -50%);
	}

	&.n-4 > :nth-child(2) {
		top: 50%;
		left: 50%;
		transform: translate(-50%, calc(-100% - 60px));
	}

	&.n-4 > :nth-child(3) {
		top: 50%;
		left: 50%;
		transform: translate(90px, -50%);
	}

	&.n-4 > :nth-child(4) {
		top: 50%;
		left: 50%;
		transform: translate(-50%, 60px);
	}

	&.n-6 > :nth-child(1) {
		top: 50%;
		left: 50%;
		transform: translate(calc(-100% - 90px), -50%);
	}

	&.n-6 > :nth-child(2) {
		top: 50%;
		left: 50%;
		transform: translate(calc(-100% - 70px), calc(-100% - 45px));
	}

	&.n-6 > :nth-child(3) {
		top: 50%;
		left: 50%;
		transform: translate(70px, calc(-100% - 45px));
	}

	&.n-6 > :nth-child(4) {
		top: 50%;
		left: 50%;
		transform: translate(90px, -50%);
	}

	&.n-6 > :nth-child(5) {
		top: 50%;
		left: 50%;
		transform: translate(70px, 45px);
	}

	&.n-6 > :nth-child(6) {
		top: 50%;
		left: 50%;
		transform: translate(calc(-100% - 70px), 45px);
	}

	&.n-8 > :nth-child(1) {
		top: 50%;
		left: 50%;
		transform: translate(calc(-100% - 90px), -50%);
	}

	&.n-8 > :nth-child(2) {
		top: 50%;
		left: 50%;
		transform: translate(calc(-100% - 70px), calc(-100% - 45px));
	}

	&.n-8 > :nth-child(3) {
		top: 50%;
		left: 50%;
		transform: translate(-50%, calc(-100% - 115px));
	}

	&.n-8 > :nth-child(4) {
		top: 50%;
		left: 50%;
		transform: translate(70px, calc(-100% - 45px));
	}

	&.n-8 > :nth-child(5) {
		top: 50%;
		left: 50%;
		transform: translate(90px, -50%);
	}

	&.n-8 > :nth-child(6) {
		top: 50%;
		left: 50%;
		transform: translate(70px, 45px);
	}

	&.n-8 > :nth-child(7) {
		top: 50%;
		left: 50%;
		transform: translate(-50%, 115px);
	}

	&.n-8 > :nth-child(8) {
		top: 50%;
		left: 50%;
		transform: translate(calc(-100% - 70px), 45px);
	}
`;

const StyledInfoContainer = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	align-items: center;
	user-select: none;
	pointer-events: none;

	transition: opacity 0.3s ease;

	&.fade-enter {
		opacity: 0;
	}

	&.fade-enter-active,
	&.fade-enter-done,
	&.fade-exit {
		opacity: 1;
	}

	&.fade-exit-active {
		opacity: 0;
	}
`;

const StyledEmptyMenuItemContainer = styled.div<{
	$isAnimating?: boolean;
	$index?: number;
	$evenCount?: number;
	$isExpanding?: boolean;
}>`
	position: absolute;
	width: 260px;
	height: 50px;
	background: #ffffff;
	opacity: 0.1;
	border-radius: 10px;
	transition:
		transform ${radialAnim.transformMs}ms cubic-bezier(0.4, 0, 0.2, 1),
		opacity ${radialAnim.transformOpacityMs}ms cubic-bezier(0.4, 0, 0.2, 1);
	will-change: transform, opacity;

	${({ $isAnimating = false, $index = 0 }) =>
		$isAnimating &&
		css`
			transition-delay: ${$index * radialAnim.stepDelayMs}ms;
		`}

	${({ $isExpanding = false, $index = 0 }) =>
		$isExpanding &&
		css`
			transition:
				background-color ${radialAnim.transformMs * 2}ms ease 0s,
				transform ${radialAnim.transformMs}ms cubic-bezier(0.4, 0, 0.2, 1)
					${$index * radialAnim.stepDelayMs}ms,
				opacity ${radialAnim.transformOpacityMs}ms
					cubic-bezier(0.17, 0.67, 0, 1.04) ${$index * radialAnim.stepDelayMs}ms;
		`}

	${({ $isAnimating, $index = 0, $evenCount = 4 }) => {
		if (!$isAnimating) return '';

		const coords = animationCoordsMap[$evenCount]?.[$index] || {
			translateX: 0,
			translateY: 0,
		};

		return css`
			transform: translate(
				${coords.translateX}px,
				${coords.translateY}px
			) !important;
			opacity: 0 !important;
		`;
	}}
`;

const StyledMenuItemContainer = styled.div<{
	$selected: boolean;
	$isAnimating?: boolean;
	$index?: number;
	$evenCount?: number;
	$isExpanding?: boolean;
}>`
	position: absolute;
	display: grid;
	grid-template-columns: 16px 1fr 20px;
	gap: 10px;
	align-items: center;
	width: 260px;
	height: 50px;
	padding: 0 20px;
	background-color: #ffffff;
	border-radius: 10px;
	/* border: 1px solid rgba(255, 255, 255, 0.1); */
	transition:
		background-color ${radialAnim.transformMs * 2}ms ease,
		transform ${radialAnim.transformMs}ms cubic-bezier(0.4, 0, 0.2, 1),
		opacity ${radialAnim.transformOpacityMs}ms cubic-bezier(0.4, 0, 0.2, 1);
	will-change: transform, opacity;

	${({ $isAnimating = false, $index = 0 }) =>
		$isAnimating &&
		css`
			transition-delay: ${$index * radialAnim.stepDelayMs}ms;
		`}

	${({ $isExpanding = false, $index = 0 }) =>
		$isExpanding &&
		css`
			transition:
				background-color ${radialAnim.transformMs * 2}ms ease 0s,
				transform ${radialAnim.transformMs}ms cubic-bezier(0.4, 0, 0.2, 1)
					${$index * radialAnim.stepDelayMs}ms,
				opacity ${radialAnim.transformOpacityMs}ms
					cubic-bezier(0.17, 0.67, 0, 1.04) ${$index * radialAnim.stepDelayMs}ms;
		`}

	${({ $isAnimating, $index = 0, $evenCount = 4 }) => {
		if (!$isAnimating) return '';

		const coords = animationCoordsMap[$evenCount]?.[$index] || {
			translateX: 0,
			translateY: 0,
		};

		return css`
			transform: translate(
				${coords.translateX}px,
				${coords.translateY}px
			) !important;
			opacity: 0 !important;
		`;
	}}

	img {
		width: 16px;
		height: 16px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
		filter: invert(100%);
	}

	& > ${Text} {
		font-weight: 500;
		font-size: 16px;
		color: #000000;
		transition: color 0.3s ease;
		margin-top: 1px;

		span {
			font-weight: 400;
			opacity: 0.5;
		}
	}

	.key {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 20px;
		height: 20px;
		background: rgba(0, 0, 0, 0.02);
		border: 1px solid rgba(0, 0, 0, 0.05);
		border-radius: 5px;
		transition:
			background-color 0.3s ease,
			border 0.3s ease;

		${Text} {
			font-weight: 700;
			font-size: 12px;
			color: #000000;
		}
	}

	${({ $selected }) =>
		$selected &&
		css`
			background-color: #2d70d9;

			img {
				filter: invert(0);
			}

			${Text} {
				color: #ffffff;
			}

			.key {
				background-color: rgba(255, 255, 255, 0.05);
				border: 1px solid rgba(255, 255, 255, 0.1);

				${Text} {
					color: #ffffff;
				}
			}
		`}
`;

const StyledSelectorContainer = styled.svg`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 60px;
	height: 60px;
`;

export {
	StyledContainer,
	StyledEmptyMenuItemContainer,
	StyledInfoContainer,
	StyledItemsCircle,
	StyledMenuItemContainer,
	StyledSelectorContainer,
};
